import { Injectable } from '@angular/core';

export enum CartErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SYNC_ERROR = 'SYNC_ERROR',
  PERSISTENCE_ERROR = 'PERSISTENCE_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface CartError {
  type: CartErrorType;
  message: string;
  context?: string;
  originalError?: any;
  timestamp: number;
  retryable: boolean;
}

export interface ErrorHandlingStrategy {
  showToast: boolean;
  logError: boolean;
  retryable: boolean;
  fallbackAction?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class CartErrorHandlerService {
  private readonly ERROR_STRATEGIES: Record<CartErrorType, ErrorHandlingStrategy> = {
    [CartErrorType.NETWORK_ERROR]: {
      showToast: true,
      logError: true,
      retryable: true,
      fallbackAction: () => this.handleNetworkErrorFallback()
    },
    [CartErrorType.VALIDATION_ERROR]: {
      showToast: true,
      logError: true,
      retryable: false,
      fallbackAction: () => this.handleValidationErrorFallback()
    },
    [CartErrorType.SYNC_ERROR]: {
      showToast: false,
      logError: true,
      retryable: true,
      fallbackAction: () => this.handleSyncErrorFallback()
    },
    [CartErrorType.PERSISTENCE_ERROR]: {
      showToast: false,
      logError: true,
      retryable: true,
      fallbackAction: () => this.handlePersistenceErrorFallback()
    },
    [CartErrorType.UNKNOWN_ERROR]: {
      showToast: true,
      logError: true,
      retryable: false,
      fallbackAction: () => this.handleUnknownErrorFallback()
    }
  };

  private errorHistory: CartError[] = [];
  private readonly MAX_ERROR_HISTORY = 50;

  handleError(error: any, context: string = 'Cart Operation'): CartError {
    const cartError = this.normalizeError(error, context);
    const strategy = this.ERROR_STRATEGIES[cartError.type];
    
    if (strategy.logError) {
      this.logError(cartError);
    }
    
    this.addToErrorHistory(cartError);
    
    if (strategy.fallbackAction) {
      try {
        strategy.fallbackAction();
      } catch (fallbackError) {
        console.error('Fallback action failed:', fallbackError);
      }
    }
    
    return cartError;
  }

  handleNetworkError(error: any, context: string = 'Network Operation'): CartError {
    const cartError: CartError = {
      type: CartErrorType.NETWORK_ERROR,
      message: this.getNetworkErrorMessage(error),
      context,
      originalError: error,
      timestamp: Date.now(),
      retryable: true
    };
    
    this.logError(cartError);
    this.addToErrorHistory(cartError);
    
    return cartError;
  }

  handleValidationError(error: any, context: string = 'Validation'): CartError {
    const cartError: CartError = {
      type: CartErrorType.VALIDATION_ERROR,
      message: this.getValidationErrorMessage(error),
      context,
      originalError: error,
      timestamp: Date.now(),
      retryable: false
    };
    
    this.logError(cartError);
    this.addToErrorHistory(cartError);
    
    return cartError;
  }

  handleSyncError(error: any, context: string = 'Sync Operation'): CartError {
    const cartError: CartError = {
      type: CartErrorType.SYNC_ERROR,
      message: this.getSyncErrorMessage(error),
      context,
      originalError: error,
      timestamp: Date.now(),
      retryable: true
    };
    
    this.logError(cartError);
    this.addToErrorHistory(cartError);
    
    return cartError;
  }

  handlePersistenceError(error: any, context: string = 'Persistence'): CartError {
    const cartError: CartError = {
      type: CartErrorType.PERSISTENCE_ERROR,
      message: this.getPersistenceErrorMessage(error),
      context,
      originalError: error,
      timestamp: Date.now(),
      retryable: true
    };
    
    this.logError(cartError);
    this.addToErrorHistory(cartError);
    
    return cartError;
  }

  getErrorHistory(): CartError[] {
    return [...this.errorHistory];
  }

  getRecentErrors(type: CartErrorType, limit: number = 10): CartError[] {
    return this.errorHistory
      .filter(error => error.type === type)
      .slice(-limit);
  }

  clearErrorHistory(): void {
    this.errorHistory = [];
  }

  hasTooManyRecentErrors(type: CartErrorType, threshold: number = 5, timeWindow: number = 60000): boolean {
    const now = Date.now();
    const recentErrors = this.errorHistory.filter(
      error => error.type === type && (now - error.timestamp) < timeWindow
    );
    
    return recentErrors.length >= threshold;
  }

  getErrorStats(): {
    total: number;
    byType: Record<CartErrorType, number>;
    retryable: number;
    recent: number;
  } {
    const now = Date.now();
    const recentThreshold = 300000; // 5 minutes
    
    const byType = Object.values(CartErrorType).reduce((acc, type) => {
      acc[type] = this.errorHistory.filter(error => error.type === type).length;
      return acc;
    }, {} as Record<CartErrorType, number>);
    
    return {
      total: this.errorHistory.length,
      byType,
      retryable: this.errorHistory.filter(error => error.retryable).length,
      recent: this.errorHistory.filter(error => (now - error.timestamp) < recentThreshold).length
    };
  }

  private normalizeError(error: any, context: string): CartError {
    if (this.isCartError(error)) {
      return error;
    }
    
    const type = this.determineErrorType(error);
    const message = this.extractErrorMessage(error);
    
    return {
      type,
      message,
      context,
      originalError: error,
      timestamp: Date.now(),
      retryable: this.isRetryableError(error)
    };
  }

  private determineErrorType(error: any): CartErrorType {
    if (this.isNetworkError(error)) {
      return CartErrorType.NETWORK_ERROR;
    }
    
    if (this.isValidationError(error)) {
      return CartErrorType.VALIDATION_ERROR;
    }
    
    if (this.isSyncError(error)) {
      return CartErrorType.SYNC_ERROR;
    }
    
    if (this.isPersistenceError(error)) {
      return CartErrorType.PERSISTENCE_ERROR;
    }
    
    return CartErrorType.UNKNOWN_ERROR;
  }

  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    if (error?.error?.message) {
      return error.error.message;
    }
    
    return 'Erro desconhecido ocorreu';
  }

  private isRetryableError(error: any): boolean {
    if (this.isNetworkError(error)) {
      return true;
    }
    
    if (this.isSyncError(error)) {
      return true;
    }
    
    if (this.isPersistenceError(error)) {
      return true;
    }
    
    return false;
  }

  private isNetworkError(error: any): boolean {
    return error?.status >= 400 && error?.status < 600 ||
           error?.code === 'NETWORK_ERROR' ||
           error?.name === 'HttpErrorResponse' ||
           error?.message?.includes('network') ||
           error?.message?.includes('timeout');
  }

  private isValidationError(error: any): boolean {
    return error?.status === 400 ||
           error?.code === 'VALIDATION_ERROR' ||
           error?.message?.includes('validation') ||
           error?.message?.includes('invalid');
  }

  private isSyncError(error: any): boolean {
    return error?.code === 'SYNC_ERROR' ||
           error?.message?.includes('sync') ||
           error?.message?.includes('synchronization');
  }

  private isPersistenceError(error: any): boolean {
    return error?.code === 'PERSISTENCE_ERROR' ||
           error?.message?.includes('localStorage') ||
           error?.message?.includes('storage') ||
           error?.message?.includes('persist');
  }

  private isCartError(error: any): error is CartError {
    return error && typeof error === 'object' && 'type' in error && 'message' in error;
  }

  private logError(error: CartError): void {
    const logMessage = `[Cart Error] ${error.type}: ${error.message}`;
    const logContext = error.context ? ` (${error.context})` : '';
    
    console.error(logMessage + logContext, {
      error: error.originalError,
      timestamp: new Date(error.timestamp).toISOString(),
      retryable: error.retryable
    });
  }

  private addToErrorHistory(error: CartError): void {
    this.errorHistory.push(error);
    
    if (this.errorHistory.length > this.MAX_ERROR_HISTORY) {
      this.errorHistory = this.errorHistory.slice(-this.MAX_ERROR_HISTORY);
    }
  }

  private handleNetworkErrorFallback(): void {
    console.warn('Network error fallback: Using cached data or offline mode');
  }

  private handleValidationErrorFallback(): void {
    console.warn('Validation error fallback: Skipping invalid operation');
  }

  private handleSyncErrorFallback(): void {
    console.warn('Sync error fallback: Will retry later');
  }

  private handlePersistenceErrorFallback(): void {
    console.warn('Persistence error fallback: Using memory storage');
  }

  private handleUnknownErrorFallback(): void {
    console.warn('Unknown error fallback: Continuing with current state');
  }

  private getNetworkErrorMessage(error: any): string {
    if (error?.status === 0) {
      return 'Erro de conexão. Verifique sua internet.';
    }
    
    if (error?.status >= 500) {
      return 'Erro no servidor. Tente novamente em alguns minutos.';
    }
    
    if (error?.status === 404) {
      return 'Recurso não encontrado.';
    }
    
    return 'Erro de rede. Tente novamente.';
  }

  private getValidationErrorMessage(error: any): string {
    return error?.message || 'Dados inválidos fornecidos.';
  }

  private getSyncErrorMessage(error: any): string {
    return error?.message || 'Erro ao sincronizar dados.';
  }

  private getPersistenceErrorMessage(error: any): string {
    return error?.message || 'Erro ao salvar dados localmente.';
  }
}
